/**
 * Single source of truth for `basePath` (must match `next.config.mjs`).
 * Use these helpers when a path might already include `/v2` (bookmarks, env, APIs, legacy links).
 */

export const APP_BASE_PATH = '/v2';

const DUP_SEGMENT = `${APP_BASE_PATH}/v2`;

/**
 * Collapse `/v2/v2`, `/v2/v2/v2`, … to a single `/v2` prefix (for middleware and absolute paths).
 */
export function collapseDuplicateAppBasePath(pathname: string): string {
  let p = pathname;
  while (true) {
    if (p === DUP_SEGMENT) {
      return APP_BASE_PATH;
    }
    if (p.startsWith(`${DUP_SEGMENT}/`)) {
      p = APP_BASE_PATH + p.slice(DUP_SEGMENT.length);
      continue;
    }
    break;
  }
  return p;
}

/**
 * For `next/link` and `router.push`, Next.js always applies `basePath`.
 * Strip a leading `/v2` if present so navigation is idempotent.
 *
 * @example pathForNextNavigation('/dashboard') → '/dashboard'
 * @example pathForNextNavigation('/v2/dashboard') → '/dashboard'
 */
export function pathForNextNavigation(path: string): string {
  if (!path) return '/';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;

  let p = path.trim();
  if (!p.startsWith('/')) p = `/${p}`;

  while (p === APP_BASE_PATH || p.startsWith(`${APP_BASE_PATH}/`)) {
    p = p === APP_BASE_PATH ? '/' : p.slice(APP_BASE_PATH.length);
    if (!p) p = '/';
    if (!p.startsWith('/')) p = `/${p}`;
  }
  return p || '/';
}

/**
 * Turn `NEXT_PUBLIC_SELF_URL` into a path for `next/link` / `router.push`.
 * A raw `<a href="https://…">` forces a full document load; the same destination via `Link` stays client-side.
 */
export function selfUrlToHomeHref(selfUrl: string | undefined): string {
  const raw = (selfUrl || '').trim();
  if (!raw) return '/';
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    try {
      const { pathname, search, hash } = new URL(raw);
      const combined = `${pathname}${search}${hash}`;
      return pathForNextNavigation(combined || '/');
    } catch {
      return '/';
    }
  }
  return pathForNextNavigation(raw.startsWith('/') ? raw : `/${raw}`);
}

/**
 * Browser path under this app: exactly one `/v2` prefix. Safe when `path` may already include `/v2`.
 *
 * @example pathWithAppBase('/dashboard') → '/v2/dashboard'
 * @example pathWithAppBase('/v2/dashboard') → '/v2/dashboard'
 * @example pathWithAppBase('/v2/v2/dashboard') → '/v2/dashboard'
 */
export function pathWithAppBase(path: string): string {
  let p = path.trim();
  if (!p.startsWith('/')) p = `/${p}`;

  const withBase =
    p === APP_BASE_PATH || p.startsWith(`${APP_BASE_PATH}/`)
      ? p
      : `${APP_BASE_PATH}${p === '/' ? '' : p}`;

  return collapseDuplicateAppBasePath(withBase);
}

/** Match `apiConfig` when env is missing (avoid `undefined` in redirects). */
const FALLBACK_SELF_ORIGIN = 'https://staging-v1-app.boostt.ai';

/**
 * `NEXT_PUBLIC_SELF_URL` should be origin only (e.g. `https://app.boostt.ai`).
 * This strips a mistaken trailing `/v2` so concatenation with `pathWithAppBase` stays correct.
 */
export function getSelfUrlOrigin(): string {
  const raw = (process.env.NEXT_PUBLIC_SELF_URL || FALLBACK_SELF_ORIGIN).trim().replace(/\/+$/, '');
  if (raw.endsWith(APP_BASE_PATH)) {
    return raw.slice(0, -APP_BASE_PATH.length).replace(/\/+$/, '') || FALLBACK_SELF_ORIGIN;
  }
  return raw;
}
